/**
 * PostalCode entity index page with search
 *
 * Uses GraphQL to fetch postal codes with state information
 * via relationship traversal (PostalCode -> District -> State).
 */

import { canonicalPath } from '@electinfo/common';
import { belowHeaderInsertPoint } from './page-header';

const POSTAL_PAGE_SIZE = 50;

/**
 * Fixed document (persisted-query list, sites#277); limit 50 = page size. Congressional districts only: since
 * rundeck#831 a ZIP also OVERLAPS its State Senate and State House districts ({st}-sd-{code}, {st}-hd-{code}),
 * which the ZIP page lists in their own sections (sites#544).
 */
const POSTAL_CODES_QUERY = `
query PostalCodes($search: String, $offset: Int!) {
  postalCodes(
    where: { zip_code: { contains: $search } }
    sort: [{ zip_code: ASC }]
    limit: 50
    offset: $offset
  ) {
    id
    zip_code
    district_count
    land_area_m2
    water_area_m2
    overlapsDistricts(where: { NOT: { OR: [{ district_slug: { contains: "-sd-" } }, { district_slug: { contains: "-hd-" } }] } }) {
      id
      state
      district_number
      type
      partOfStates {
        id
        name
        state
      }
    }
  }
  postalCodesConnection(where: { zip_code: { contains: $search } }) {
    totalCount
  }
}
`;

interface GraphQLRequest {
  query: string;
  variables: Record<string, unknown>;
}

interface PostalDistrictResult {
  id: string;
  state?: string;
  district_number?: number;
  type?: string;
  partOfStates?: Array<{
    id?: string;
    name?: string;
    state?: string;
  }>;
}

interface PostalCodeResult {
  id: string;
  zip_code: string;
  district_count?: number;
  land_area_m2?: number;
  water_area_m2?: number;
  overlapsDistricts: PostalDistrictResult[];
}

interface PostalGraphQLResponse {
  data?: {
    postalCodes: PostalCodeResult[];
    postalCodesConnection?: {
      totalCount: number;
    };
  };
  errors?: Array<{ message: string }>;
}

class PostalCodeIndex {
  private currentQuery = '';
  private currentPage = 1;
  private totalResults = 0;

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  private async setup(): Promise<void> {
    this.injectStyles();
    this.insertUI();
    this.loadFromURL();
  }

  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .postal-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        margin-bottom: 20px;
        border: 1px solid var(--border, #e2e8f0);
      }
      .postal-search-form {
        display: flex;
        gap: 10px;
        margin-bottom: 15px;
      }
      .postal-search-form input {
        flex: 1;
        padding: 12px 16px;
        font-size: 1rem;
        border: 2px solid var(--border, #e2e8f0);
        border-radius: 6px;
        font-family: inherit;
      }
      .postal-search-form input:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
      .postal-search-form button {
        padding: 12px 24px;
        font-size: 1rem;
        background: var(--secondary, #2c5282);
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-family: inherit;
      }
      .postal-search-form button:hover {
        background: var(--primary, #1a365d);
      }
      .postal-results {
        margin-top: 15px;
      }
      .postal-results-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--border, #e2e8f0);
      }
      .postal-results-header h3 {
        margin: 0;
        color: var(--primary, #1a365d);
      }
      .postal-clear {
        background: none;
        border: 1px solid var(--border, #e2e8f0);
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
      }
      .postal-clear:hover {
        background: var(--bg, #f7fafc);
      }
      .postal-result {
        display: block;
        padding: 12px 16px;
        border: 1px solid var(--border, #e2e8f0);
        border-radius: 6px;
        margin-bottom: 8px;
        text-decoration: none;
        color: inherit;
      }
      .postal-result:hover {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .postal-result-name {
        font-weight: 600;
        color: var(--primary, #1a365d);
      }
      .postal-result-meta {
        font-size: 0.9em;
        color: #718096;
        margin-top: 2px;
      }
      .postal-message {
        padding: 20px;
        text-align: center;
        color: #718096;
      }
      .postal-error {
        color: #c53030;
        background: #fff5f5;
        border-radius: 6px;
      }
      .postal-loading {
        text-align: center;
        padding: 40px;
        color: #718096;
      }
      .postal-loading::after {
        content: '';
        display: inline-block;
        width: 20px;
        height: 20px;
        border: 2px solid #e2e8f0;
        border-top-color: var(--secondary, #2c5282);
        border-radius: 50%;
        animation: postal-spin 0.8s linear infinite;
        margin-left: 10px;
        vertical-align: middle;
      }
      @keyframes postal-spin {
        to { transform: rotate(360deg); }
      }
      .postal-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid var(--border, #e2e8f0);
        flex-wrap: wrap;
      }
      .postal-pagination-info {
        width: 100%;
        text-align: center;
        color: #718096;
        font-size: 0.9em;
        margin-bottom: 10px;
      }
      .postal-pagination button {
        padding: 8px 12px;
        border: 1px solid var(--border, #e2e8f0);
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
        min-width: 40px;
      }
      .postal-pagination button:hover:not(:disabled) {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .postal-pagination button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .postal-pagination button.active {
        background: var(--secondary, #2c5282);
        color: white;
        border-color: var(--secondary, #2c5282);
      }
      .postal-index-hidden {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  private insertUI(): void {
    const container = document.createElement('div');
    container.className = 'postal-container';

    container.innerHTML = `
      <form class="postal-search-form" id="postal-search-form">
        <input type="text" id="postal-search-input" placeholder="Search postal codes (e.g., 85001)..." autocomplete="off">
        <button type="submit">Search</button>
      </form>
      <div class="postal-results" id="postal-results">
        <div class="postal-results-header">
          <h3 id="postal-results-title">All Postal Codes</h3>
          <button type="button" class="postal-clear" id="postal-clear" style="display: none;">Clear</button>
        </div>
        <div id="postal-results-list"></div>
        <div id="postal-pagination"></div>
      </div>
    `;

    const listView = document.getElementById('list-view');
    if (listView) {
      listView.appendChild(container);
    } else {
      const insertPoint = belowHeaderInsertPoint();
      if (insertPoint?.parentNode) {
        insertPoint.parentNode.insertBefore(container, insertPoint);
      }
    }

    // Hide any existing static content
    document.querySelectorAll('.alphabetical-index, section > ul, main > section').forEach(el => {
      el.classList.add('postal-index-hidden');
    });

    // Event listeners
    document.getElementById('postal-search-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = document.getElementById('postal-search-input') as HTMLInputElement;
      this.loadPostalCodes({ query: input?.value.trim() || '', page: 1 });
    });

    document.getElementById('postal-clear')?.addEventListener('click', () => {
      const searchInput = document.getElementById('postal-search-input') as HTMLInputElement;
      if (searchInput) searchInput.value = '';
      this.loadPostalCodes({ query: '', page: 1 });
    });

    window.addEventListener('popstate', () => this.loadFromURL());
  }

  private loadFromURL(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q') || '';
    const page = parseInt(urlParams.get('page') || '1', 10) || 1;

    const searchInput = document.getElementById('postal-search-input') as HTMLInputElement;
    if (searchInput) searchInput.value = query;

    this.loadPostalCodes({ query, page, updateHistory: false });
  }

  private async loadPostalCodes(options: { query?: string; page?: number; updateHistory?: boolean } = {}): Promise<void> {
    const query = options.query ?? this.currentQuery;
    const page = options.page || 1;
    const updateHistory = options.updateHistory !== false;

    const resultsList = document.getElementById('postal-results-list');
    const paginationContainer = document.getElementById('postal-pagination');
    const resultsTitle = document.getElementById('postal-results-title');
    const clearButton = document.getElementById('postal-clear');

    this.currentQuery = query;
    this.currentPage = page;

    if (resultsList) resultsList.innerHTML = '<div class="postal-loading">Loading</div>';
    if (paginationContainer) paginationContainer.innerHTML = '';

    // Update title
    const title = query ? `Search: "${query}"` : 'All Postal Codes';
    if (resultsTitle) resultsTitle.textContent = title;
    if (clearButton) clearButton.style.display = query ? 'block' : 'none';

    if (updateHistory) {
      this.updateURL(query, page);
    }

    const graphqlQuery = this.buildGraphQLQuery(query, page);

    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(graphqlQuery)
      });

      if (!res.ok) {
        if (resultsList) resultsList.innerHTML = `<p class="postal-message postal-error">Failed to load: ${res.status}</p>`;
        return;
      }

      const response: PostalGraphQLResponse = await res.json();

      if (response.errors) {
        if (resultsList) resultsList.innerHTML = `<p class="postal-message postal-error">Error: ${response.errors[0]?.message || 'GraphQL error'}</p>`;
        return;
      }

      const postalCodes = response.data?.postalCodes || [];
      this.totalResults = response.data?.postalCodesConnection?.totalCount || postalCodes.length;

      this.displayResults(postalCodes);
      this.displayPagination(page, this.totalResults);

    } catch (err) {
      if (resultsList) resultsList.innerHTML = `<p class="postal-message postal-error">Error: ${(err as Error).message}</p>`;
    }
  }

  private buildGraphQLQuery(searchQuery: string, page: number): GraphQLRequest {
    return {
      query: POSTAL_CODES_QUERY,
      variables: {
        offset: (page - 1) * POSTAL_PAGE_SIZE,
        ...(searchQuery && { search: searchQuery })
      }
    };
  }

  private displayResults(postalCodes: PostalCodeResult[]): void {
    const resultsList = document.getElementById('postal-results-list');
    if (!resultsList) return;

    if (!postalCodes.length) {
      resultsList.innerHTML = '<p class="postal-message">No postal codes found.</p>';
      return;
    }

    const html = postalCodes.map(pc => {
      const code = pc.zip_code || pc.id || '';
      // "ZZ" is the Census code for area in no congressional district (water, e.g. Lake Michigan).
      const districts = (pc.overlapsDistricts || []).filter(d => String(d.district_number) !== 'ZZ');

      // Get state info from relationship traversal
      const states = this.getStatesFromDistricts(districts);

      // Get formatted district list
      const districtList = this.formatDistrictList(districts);

      // Build metadata string: "California · CA-12, CA-13"
      const metaParts: string[] = [];
      if (states.length > 0) {
        metaParts.push(states.join(', '));
      }
      if (districtList) {
        metaParts.push(districtList);
      }

      const meta = metaParts.length > 0 ? metaParts.join(' · ') : 'Postal Code';
      const url = `/${canonicalPath('PostalCode', pc.id)}/`;

      return `
        <a href="${url}" class="postal-result">
          <div class="postal-result-name">${this.escapeHtml(code)}</div>
          <div class="postal-result-meta">${this.escapeHtml(meta)}</div>
        </a>
      `;
    }).join('');

    resultsList.innerHTML = html;
  }

  private getStatesFromDistricts(districts: PostalDistrictResult[]): string[] {
    if (!districts || districts.length === 0) return [];

    const stateSet = new Set<string>();

    for (const district of districts) {
      // Try to get state name from relationship
      if (district.partOfStates?.[0]?.name) {
        stateSet.add(district.partOfStates[0].name);
      } else if (district.state) {
        // Fall back to state code on district
        stateSet.add(district.state);
      }
    }

    return Array.from(stateSet).sort();
  }

  private formatDistrictList(districts: PostalDistrictResult[]): string {
    if (!districts || districts.length === 0) return '';

    // Format as "CA-12, CA-13" or "TX-01, TX-02, TX-03"
    const districtNames = districts.map(d => {
      const state = d.state || '';
      const num = d.district_number != null ? String(d.district_number).padStart(2, '0') : '';
      return num ? `${state}-${num}` : state;
    }).filter(Boolean).sort();

    // If more than 3 districts, show first 3 + count
    if (districtNames.length > 3) {
      return `${districtNames.slice(0, 3).join(', ')} +${districtNames.length - 3} more`;
    }

    return districtNames.join(', ');
  }

  private displayPagination(page: number, total: number): void {
    const paginationContainer = document.getElementById('postal-pagination');
    if (!paginationContainer) return;

    const totalPages = Math.ceil(total / POSTAL_PAGE_SIZE);

    if (totalPages <= 1) {
      paginationContainer.innerHTML = total > 0
        ? `<div class="postal-pagination-info">Showing all ${total.toLocaleString()} postal codes</div>`
        : '';
      return;
    }

    const start = (page - 1) * POSTAL_PAGE_SIZE + 1;
    const end = Math.min(page * POSTAL_PAGE_SIZE, total);

    let html = `<div class="postal-pagination-info">Showing ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()} postal codes</div>`;
    html += '<div class="postal-pagination">';
    html += `<button type="button" data-page="1" ${page === 1 ? 'disabled' : ''}>First</button>`;
    html += `<button type="button" data-page="${page - 1}" ${page === 1 ? 'disabled' : ''}>Prev</button>`;

    const pageNumbers = this.getPageNumbers(page, totalPages);
    let lastNum = 0;

    for (const num of pageNumbers) {
      if (num - lastNum > 1) {
        html += '<span style="padding: 8px 4px; color: #718096;">...</span>';
      }
      html += `<button type="button" data-page="${num}" class="${num === page ? 'active' : ''}">${num}</button>`;
      lastNum = num;
    }

    html += `<button type="button" data-page="${page + 1}" ${page === totalPages ? 'disabled' : ''}>Next</button>`;
    html += `<button type="button" data-page="${totalPages}" ${page === totalPages ? 'disabled' : ''}>Last</button>`;
    html += '</div>';

    paginationContainer.innerHTML = html;

    paginationContainer.querySelectorAll('button[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetPage = parseInt((btn as HTMLButtonElement).dataset.page || '1', 10);
        if (targetPage >= 1 && targetPage <= totalPages && targetPage !== page) {
          this.loadPostalCodes({ page: targetPage });
          document.getElementById('postal-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  private getPageNumbers(current: number, total: number): number[] {
    const pages: number[] = [];
    const delta = 2;

    pages.push(1);
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      pages.push(i);
    }
    if (total > 1) pages.push(total);

    return [...new Set(pages)].sort((a, b) => a - b);
  }

  private updateURL(query: string, page: number): void {
    const url = new URL(window.location.href);

    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    window.history.pushState({ query, page }, '', url.toString());
  }

  private escapeHtml(str: string | undefined): string {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

// Auto-initialize
new PostalCodeIndex();
